import React, { useState, useRef, useCallback, useEffect } from 'react';
import cn from 'classnames';
import { Editor } from 'draft-js';

import { editorStateForEditorComponent } from 'Shared/utils/draftJsHelpers';
import { createFormField } from 'Shared/utils/createFormField';
import DraftEditor from 'Shared/components/DraftEditor/DraftEditor';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import './index.scss';

type Props = {
  value: Editor;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  editorClassName?: string;
  touched: boolean;
  error: string;
  errorLabel?: boolean;
  onFocus?: Function;
  onBlur?: Function;
  onChange: Function;
  onAssignedPersonAdd?: Function;
};

function TextEditor({
  value,
  onChange,
  placeholder,
  className = '',
  editorClassName = '',
  touched,
  error,
  errorLabel,
  onFocus,
  onBlur,
  onAssignedPersonAdd,
  autoFocus,
}: Props) {
  const { tr } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const draftEditorEl = useRef(null);
  const effectivePlaceholder = placeholder ?? tr('common.placeholders.enter_text');

  const editorState = !value || typeof value === 'string' ? editorStateForEditorComponent(value) : value;

  const focusTextEditor = useCallback(() => {
    setIsFocused(true);
    setTimeout(() => draftEditorEl.current?.focus(), 0);
    if (onFocus) onFocus();
  }, [draftEditorEl, onFocus]);

  const handleClick = useCallback(() => focusTextEditor(), [focusTextEditor]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (onBlur) onBlur();
  }, [onBlur]);

  useEffect(() => {
    const node = draftEditorEl.current;
    if (node && autoFocus) {
      focusTextEditor();
    }
    if (!value || typeof value === 'string') onChange(editorStateForEditorComponent(value));
  }, []);

  return (
    <div className={cn('w-100 form-group', className)}>
      <div
        className={cn(`FormTextEditor ${editorClassName}`, {
          'FormTextEditor--focused': isFocused,
          'FormTextEditor--is-invalid': touched && error,
        })}
        onClick={handleClick}
      >
        <DraftEditor
          isInlineAttachments
          placeholder={effectivePlaceholder}
          readOnly={!isFocused}
          editorRef={draftEditorEl}
          editorState={editorState}
          onChange={onChange}
          onAssignedPersonAdd={onAssignedPersonAdd}
          onBlur={handleBlur}
        />
      </div>
      {touched && error && errorLabel ? <p className="form-input-error text-danger">{error}</p> : null}
    </div>
  );
}

export default createFormField(TextEditor);
